import { Button, DialogContent, DialogTitle, Stack, Typography } from "@mui/material";
import { useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import DialogButtons from "@entrilia/shared/components/DialogButtons";
import DialogCloseButton from "@entrilia/shared/components/DialogCloseButton";
import SmallDialog from "@entrilia/shared/components/SmallDialog";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import useDefinedContext from "@entrilia/shared/hooks/useDefinedContext";
import DeleteDialogIcon from "@entrilia/shared/icons/DeleteDialogIcon";
import { logError } from "@entrilia/shared/logging";
import adminApi from "../../../../api/adminApi";
import type { Inbox } from "../../../../api/types/inboxesTypes";
import AgentInboxSettingsContext from "./AgentInboxSettingsContext";

const updateInbox = withErrorHandling(adminApi.updateInbox);

interface Props {
  channelId: string;
  onClose: () => void;
  onDeleted: (updatedInbox: Inbox) => void;
}

const DeleteInboxChannelDialog = ({ channelId, onClose, onDeleted }: Props) => {
  const { inbox } = useDefinedContext(AgentInboxSettingsContext);
  const { sendNotification, sendNotificationError } = useNotificationContext();

  const [loading, setLoading] = useState(false);

  const channel = inbox.channels.find((c) => c.id === channelId);

  if (channel === undefined) {
    return null;
  }

  const handleDelete = async () => {
    const payload = {
      channels: inbox.channels
        .filter((c) => c.id !== channelId)
        .map((channel) => ({ ...channel, isNewChannel: false })),
    };

    setLoading(true);
    const [resp, error] = await updateInbox(inbox.id, payload);
    setLoading(false);

    if (error) {
      logError(error, "[DeleteInboxChannelDialog] updateInbox");
      sendNotificationError(error.message);
      return;
    }

    sendNotification("Inbox channel deleted successfully");
    onDeleted(resp);
    onClose();
  };

  return (
    <SmallDialog open onClose={onClose}>
      <DialogTitle>Delete Inbox Channel</DialogTitle>
      <DialogCloseButton onClick={onClose} />
      <DialogContent>
        <Stack spacing={2} sx={{ alignItems: "center" }}>
          <DeleteDialogIcon sx={{ width: 80, height: 80 }} />
          <Stack spacing={1} sx={{ alignItems: "center" }}>
            <Typography variant="h6">Delete {channel.name}?</Typography>
            <Typography color="textSecondary" sx={{ textAlign: "center" }}>
              You&apos;re about to delete this channel. This action is permanent and can&apos;t be undone.
            </Typography>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogButtons onCancel={onClose}>
        <Button variant="outlined" color="error" loading={loading} onClick={handleDelete}>
          Delete
        </Button>
      </DialogButtons>
    </SmallDialog>
  );
};

export default DeleteInboxChannelDialog;
